import { Box } from '@mui/material';
import { CompanyRole } from 'constants/CompanyRole';
import { NavigationDirection } from 'constants/NavigationDirection';
import { Dayjs } from 'dayjs';
import { toNumber } from 'lodash';
import dayjs from 'utils/dayjs';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { getAvailabilitiesAction, updateAvailabilitiesAction } from 'store/actions/availabilities-actions';
import { availabilitySelector } from 'store/selectors/availabilities-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import { IAvailability, ICalendarDay } from 'types/availabilities';
import { generateFullMonthDays, getNumberOfNewChanges } from '../utils/helpers';
import AvailabilitiesGreenBar from './green-bar';
import FillAvailabilityModal from './modal';
import {
  AvailabilitiesContainer,
  AvailabilitiesNavigationContainer,
  ScaledUpLeftArrow,
  ScaledUpRightArrow,
} from './styles';
import AvailabilitiesTable from './table';
import { AvailabilitiesContextProps } from './types';

export const AvailabilitiesContext = createContext<AvailabilitiesContextProps>({
  currentMonth: 0,
  currentYear: 0,
  openAvailabilityFillModal: null,
  availability: null,
});

const Availabilities = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const companyId = useAppSelector(profileSelector)?.currentCompany?.toString();
  const availability = useAppSelector(availabilitySelector);

  const user = useAppSelector(profileSelector);
  const userCompanyId = user?.companyId || undefined;
  const [currentMonthView, setCurrentMonthView] = useState<number>(dayjs().month());
  const [fillModalOpen, setFillModalOpen] = useState<boolean>(false);
  const [selectedDay, setSelectedDay] = useState<Dayjs>(dayjs());
  const [firstDayOfCalendar, setFirstDayOfCalendar] = useState<Dayjs>(dayjs());
  const [calendarDays, setCalendarDays] = useState<ICalendarDay[]>([]);
  const [currentAvailability, setCurrentAvailability] = useState<IAvailability>(availability);
  const [year, setYear] = useState<number>(dayjs().year());

  const canAccessAvailabilities = useCallback((): boolean => {
    return user?.featureAvailabilityEnabled ?? false;
  }, [user]);

  useEffect(() => {
    if (user?.role !== CompanyRole.EMPLOYEE || !userCompanyId || !canAccessAvailabilities()) {
      window.location.href = '/';
    }
  }, [user, userCompanyId, canAccessAvailabilities]);

  const isSubmissionNeeded = useMemo(() => {
    if (!currentAvailability?.isSubmitted) {
      return true;
    }
    return false;
  }, [currentAvailability]);

  const numberOfChanges = useMemo(() => {
    if (!availability?.days || !currentAvailability?.days) {
      return 0;
    }
    return getNumberOfNewChanges(availability, currentAvailability);
  }, [availability, currentAvailability]);

  const changeMonth = (navigationDirection: NavigationDirection) => {
    const newMonth = navigationDirection === NavigationDirection.NEXT ? currentMonthView + 1 : currentMonthView - 1;
    if (newMonth < 0) {
      setCurrentMonthView(11);
      setYear(year - 1);
      return;
    }
    if (newMonth > 11) {
      setCurrentMonthView(0);
      setYear(year + 1);
      return;
    }
    setCurrentMonthView(newMonth);
  };

  const changeDayOnMobile = (day: Dayjs, direction: number) => {
    if (direction === 1) {
      setSelectedDay(day.clone().add(1, 'days'));
    } else {
      setSelectedDay(day.clone().subtract(1, 'days'));
    }
  };

  const onSubmitAvailability = () => {
    dispatch(
      updateAvailabilitiesAction({
        availability: currentAvailability,
      }),
    );
  };

  const openAvailabilityFillModal = useCallback(
    (day: Dayjs) => {
      setFillModalOpen(true);
      setFirstDayOfCalendar(dayjs().month(currentMonthView).year(year).startOf('month').startOf('week'));
      setSelectedDay(day);
      setCalendarDays(prevState => {
        return prevState.map(calendarDay => {
          if (calendarDay.date.isSame(day, 'day')) {
            return { ...calendarDay, checked: true };
          }
          return { ...calendarDay, checked: false };
        });
      });
    },
    [currentMonthView, year],
  );

  useEffect(() => {
    setCalendarDays(generateFullMonthDays(currentMonthView, year));
    setCurrentAvailability(availability);
  }, [currentMonthView, availability, year]);

  useEffect(() => {
    dispatch(
      getAvailabilitiesAction({
        month: currentMonthView + 1,
        year,
        companyId: toNumber(userCompanyId),
      }),
    );
  }, [dispatch, year, currentMonthView, userCompanyId]);

  const contextValue = useMemo((): AvailabilitiesContextProps => {
    return {
      currentMonth: currentMonthView,
      currentYear: year,
      openAvailabilityFillModal: (day: Dayjs) => openAvailabilityFillModal(day),
      availability: currentAvailability,
    };
  }, [currentMonthView, currentAvailability, openAvailabilityFillModal, year]);

  return (
    <Box>
      <AvailabilitiesContainer>
        <AvailabilitiesNavigationContainer>
          <ScaledUpLeftArrow onClick={() => changeMonth(NavigationDirection.PREVIOUS)} />
        </AvailabilitiesNavigationContainer>
        <AvailabilitiesContext.Provider value={contextValue}>
          <AvailabilitiesTable onChangeMonth={changeMonth} />
        </AvailabilitiesContext.Provider>
        <AvailabilitiesNavigationContainer>
          <ScaledUpRightArrow onClick={() => changeMonth(NavigationDirection.NEXT)} />
        </AvailabilitiesNavigationContainer>

        {fillModalOpen && (
          <FillAvailabilityModal
            day={selectedDay}
            onClose={() => setFillModalOpen(false)}
            calendarDays={calendarDays}
            onChangeDays={setCalendarDays}
            changeDayOnMobile={changeDayOnMobile}
            onChangeAvailability={setCurrentAvailability}
            availability={currentAvailability}
          />
        )}
      </AvailabilitiesContainer>
      {isSubmissionNeeded && (
        <AvailabilitiesGreenBar
          onSubmit={onSubmitAvailability}
          isSubmissionNeeded={isSubmissionNeeded}
          numberOfChanges={numberOfChanges}
        />
      )}
    </Box>
  );
};

export default Availabilities;
